import { AlertCircle, CheckCircle2, Clock, Cpu, Loader2, SquareArrowOutUpRight } from "lucide-react";
import { Button } from "@/client/components/ui/button";
import { ScrollArea } from "@/client/components/ui/scroll-area";
import { useGpu, useStoryJobs, type ImageJobView } from "@/client/hooks/use-art";
import { useSession } from "@/client/hooks/use-session";
import { useTabs } from "@/client/hooks/use-tabs-context";
import { cn } from "@/client/lib/utils";
import { GPU_SECTION_ID } from "./gpu-status";
import { ClaimButton, jobElementId, jobStatusText, jobTitle } from "./job-card";

const GROUPS: { title: string; icon: typeof Clock; match: (job: ImageJobView) => boolean }[] = [
  { title: "Ready to review", icon: CheckCircle2, match: (job) => job.status === "review" },
  { title: "Running", icon: Loader2, match: (job) => job.status === "running" },
  { title: "Waiting for a GPU", icon: Clock, match: (job) => job.status === "queued" || job.status === "claimed" },
  { title: "Failed", icon: AlertCircle, match: (job) => job.status === "failed" },
];

/** Jobs that want someone's attention: waiting for a GPU, or back for review */
export function artAttentionCount(jobs: ImageJobView[] | null): number {
  return (jobs ?? []).filter((job) => job.status === "queued" || job.status === "review").length;
}

/**
 * The navigator's Art: your GPU at a glance and the story's image jobs.
 * Everything opens in the Art tab, where jobs are reviewed.
 */
export function ArtPanel({ storyId }: { storyId: string }) {
  const jobs = useStoryJobs(storyId) ?? [];
  const gpu = useGpu();
  const { user } = useSession();
  const { openArtTab, activeTab } = useTabs();
  const worker = gpu?.worker ?? null;

  /** Opens the Art tab, scrolled to a job or to the GPU section */
  const openJob = (target?: string) => {
    openArtTab(storyId);
    if (!target) return;
    // The tab mounts on the next frame; then bring the job into view
    const elementId = target === GPU_SECTION_ID ? target : jobElementId(target);
    setTimeout(() => document.getElementById(elementId)?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
  };

  return (
    <div className="flex h-full flex-col">
      <div data-region="panel-header" className="flex items-center justify-between gap-2 border-b p-3">
        <h2 className="text-sm font-semibold">Art</h2>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          onClick={() => openJob()}
          aria-label="Open the Art view"
          title="Open the Art view"
        >
          <SquareArrowOutUpRight className="size-4" />
        </Button>
      </div>

      <button
        type="button"
        onClick={() => openJob(worker?.online ? undefined : GPU_SECTION_ID)}
        className={cn(
          "flex items-center gap-2 border-b px-3 py-2 text-left text-xs text-muted-foreground hover:bg-accent",
          activeTab?.type === "art" && "bg-primary/5"
        )}
      >
        <Cpu className="size-3.5 shrink-0" />
        <span
          className={cn(
            "size-2 shrink-0 rounded-full",
            worker?.online ? "bg-green-500" : worker ? "bg-muted-foreground/50" : "border border-muted-foreground/50"
          )}
        />
        <span className="min-w-0 flex-1 truncate">
          {worker ? (worker.online ? "Your GPU is online" : "Your GPU is offline") : "No GPU yet. Set one up"}
        </span>
      </button>

      <ScrollArea className="min-h-0 flex-1">
        {jobs.length === 0 ? (
          <div className="space-y-3 p-6 text-center text-sm text-muted-foreground">
            <p>No image jobs. Generate art from a character or location, or from the Art view.</p>
            <Button size="sm" variant="outline" onClick={() => openJob()}>
              Open Art
            </Button>
          </div>
        ) : (
          <div className="space-y-3 p-2">
            {GROUPS.map((group) => {
              const matching = jobs.filter(group.match);
              if (matching.length === 0) return null;
              const Icon = group.icon;
              return (
                <section key={group.title}>
                  <h3 className="flex items-center gap-1.5 px-2 pb-1 text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
                    <Icon className={cn("size-3", group.title === "Running" && "animate-spin")} />
                    {group.title}
                    <span className="font-normal">{matching.length}</span>
                  </h3>
                  <ul className="space-y-0.5">
                    {matching.map((job) => (
                      <li key={job.id} className="rounded-sm hover:bg-accent">
                        <button type="button" onClick={() => openJob(job.id)} className="block w-full px-2 pt-1.5 pb-1 text-left">
                          <div className="truncate text-sm">
                            {jobTitle(job)}
                          </div>
                          <div className={cn("truncate text-xs text-muted-foreground", job.status === "failed" && "text-destructive")}>
                            {jobStatusText(job, user?.id)}
                          </div>
                        </button>
                        {job.status === "running" && (
                          <div className="mx-2 mb-1.5 h-0.5 overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full bg-primary transition-all"
                              style={{ width: `${Math.round((job.progress.done / Math.max(1, job.progress.total)) * 100)}%` }}
                            />
                          </div>
                        )}
                        {job.status === "queued" && (
                          <div className="px-2 pb-1.5">
                            <ClaimButton job={job} size="xs" />
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
